<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useFormatters } from '@lib/composables/useFormatters'
import BBadge from '@lib/components/base/BBadge.vue'
import EventStatusBadge from './EventStatusBadge.vue'
import { computeStatus } from '@/lib/eventStatus'
import type { Event } from '@/composables/useEventsData'

defineProps<{
    event: Event
    selected: boolean
    expanded: boolean
}>()

defineEmits<{
    (e: 'toggle-select'): void
    (e: 'toggle-expand'): void
}>()

const { t } = useI18n()
const fmt = useFormatters()
</script>

<template>
    <article
        class="bg-white border border-gray-200 rounded-12 p-3 flex flex-col gap-2 transition"
        :class="{ 'ring-2 ring-teal': selected, 'border-teal': expanded }"
    >
        <div class="flex items-start gap-2">
            <!--
                Checkbox handles selection. .stop on click prevents the
                surrounding card's expand-toggle from firing.
            -->
            <input
                type="checkbox"
                :checked="selected"
                class="mt-1 w-5 h-5 accent-teal cursor-pointer shrink-0"
                @click.stop="$emit('toggle-select')"
            />
            <button
                type="button"
                class="flex-1 min-w-0 text-left"
                @click="$emit('toggle-expand')"
            >
                <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-semibold text-ink truncate">{{ event.name }}</span>
                    <BBadge
                        v-if="(event.bzucoId ?? 0) > 0"
                        variant="arch"
                        :label="t('events.bzucoSynced.badge')"
                    />
                </div>
                <div class="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-500 tabular-nums">
                    <div>
                        <span class="text-gray-400">{{ t('events.list.cols.startAt') }}: </span>
                        <span v-if="event.startAt">{{ fmt.dateTime(event.startAt) }}</span>
                        <span v-else class="text-gray-300">—</span>
                    </div>
                    <div>
                        <span class="text-gray-400">{{ t('events.list.cols.endAt') }}: </span>
                        <span v-if="event.endAt">{{ fmt.dateTime(event.endAt) }}</span>
                        <span v-else class="text-gray-300">—</span>
                    </div>
                </div>
                <div class="mt-1.5">
                    <EventStatusBadge :status="computeStatus(event.startAt, event.endAt)" />
                </div>
            </button>
        </div>
    </article>
</template>
